import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TableSortLabel,
  Avatar,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Popover,
  Divider,
  Skeleton,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
// Close stays plain MUI (@mui/icons-material/Close) -- the app-wide "simple
// cross" dialog-close convention confirmed on the AI Agents pass (Knowledge
// Sources drawer/View Document dialog both reverted FROM iconsax's
// CloseCircle back to this exact icon) -- CloseSquare below is retired to
// match, not kept as a Reports-only exception.
import CloseIcon from "@mui/icons-material/Close";
import FormControlLabel from "@mui/material/FormControlLabel";
// Iconsax, same library as the LeftNav rail / AI Agents pass (2026-09-04:
// "remake the icons to match our new ui like we did for ai pages") --
// TickSquare already IS a rounded-square-with-checkmark glyph, exactly the
// shape asked for, not a filled circle. ArrowDown2/More/Profile2User added
// this pass to replace the last plain-MUI icons in this file; CheckBox(
// OutlineBlank)/PeopleAltOutlined/KeyboardArrowDown/MoreVert/CloseSquare
// retired.
import { TickSquare, ArrowLeft2 as BackIcon, ElementEqual as CustomizeColumnsIcon, ArrowDown2, More as MoreIcon, Profile2User } from "iconsax-react";
import { fetchReports } from "../../redux/Slice/Reports/ReportsSlice";
import { fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import { fetchActiveMembers, fetchTeams } from "../../redux/Slice/Settings/settingsSlice";
import AuthView from "../../utils/AuthView";
// Reusing the Tasks table's own background/alignment treatment (Rahim's
// explicit reference) instead of re-deriving it -- same shared util Tasks
// itself uses, not a duplicate. Columns/fields here are untouched.
import { taskTableOuterBorder } from "../../utils/taskTableGridSx";
import { reportsFilterPillSx, REPORTS_PILL_HEIGHT, REPORTS_PILL_FONT_SIZE, reportsDropdownPaperSx, reportsDropdownSelectedSx, reportsInfoTooltipSx } from "./reportsFilterStyles";
import { REPORTS_TOOLTIP_COPY } from "./reportsTooltipCopy";
import ReportsDateRangeControl from "./ReportsDateRangeControl";
import ReportsExportButton from "./ReportsExportButton";
import CategoryReportDetail from "./CategoryReportDetail";
import { CATEGORY_CONFIG } from "./categoryReportsConfig";
import DurationOperatorFilter from "./DurationOperatorFilter";
import { downloadCsv } from "./reportsExportCsv";

// Same square-tick convention the Customize Columns dialog's own checkboxes
// use further down this file (2026-08-17) -- reused here instead of MUI's
// circle-free but still generic checkbox icons, so the two checkbox
// treatments in this one file no longer look like two different libraries.
const checkboxUncheckedIcon = (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: "5px",
      border: (theme) => `1.5px solid ${theme.palette.divider}`,
    }}
  />
);
const checkboxCheckedIcon = (
  <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", color: "primary.main" }}>
    <TickSquare variant="Bold" size="100%" color="currentColor" />
  </Box>
);

// Filter card styling — same elevated "pill" convention already used for the
// Tasks toolbar (TasksActions.jsx's actionsBarSx): rounded bordered card,
// subtle shadow in light mode, none in dark. Reused here so Reports' filter
// bar reads as the same design language, not a one-off.
// Plain layout row now -- no shared card background/border/shadow wrapping
// the controls. Each control (Date Range, All members, etc.) carries its
// own individual white background/border instead, so they read as separate
// floating pill buttons on the page, not one grouped bar.
const reportsFilterCardSx = {
  display: "flex",
  flexDirection: { xs: "column", lg: "row" },
  alignItems: { xs: "stretch", lg: "center" },
  flexWrap: { lg: "wrap" },
  gap: { xs: 1, lg: 1.25 },
  mb: { xs: 2, sm: 2.5 },
  // Right inset so Export doesn't sit flush against the page edge --
  // matches the table Paper's own right padding below it.
  // Halved (Rahim's screenshot, 2026-08-17): now that boxSizing:border-box
  // (below) makes this bar's own box genuinely match the card's box, the
  // old pr value (matched to Paper's px, meant for TABLE content insets)
  // read as too much empty space before Export -- this pr is the ONLY
  // thing between Export and the bar's true right edge, unlike Paper's px
  // which insets actual table columns, so it didn't need to be as large.
  pr: { xs: 0.75, sm: 1, md: 1, lg: 1.25, xl: 1.5 },
  // Missing boxSizing was the actual cause of the maxWidth/mx misalignment
  // between this bar and the table card below it (Rahim's bug report,
  // 2026-08-17, live-measured): without border-box, this box's own
  // maxWidth/width cap the CONTENT area only, and padding (pr above) adds
  // on top of that instead of being included in it -- pr alone pushed the
  // whole box 24px past where the card (which already has border-box) caps
  // out, so Export sat outside the card's own right edge while everything
  // still looked "roughly aligned" enough to be confusing. Paper already
  // had this set explicitly; this bar just never did.
  boxSizing: "border-box",
};

// Pill style + sizing constants now live in reportsFilterStyles.js (shared
// with CategoryReportDetail.jsx's "Add filter" button + ReportsDateRangeControl's
// Date Range button) -- imported above, not redefined here.

// Small section-header label used inside the combined Response/Resolution
// Time column-header dropdown ("Filter" / "Display as").
const reportsDropdownSectionLabelSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  mb: 0.75,
};

// No green in this palette on purpose -- Rahim, 2026-08-17: member avatar
// colour shouldn't read as the brand green accent.
const avatarColors = [
  "#6366f1", "#ec4899", "#f59e0b", "#3b82f6",
  "#8b5cf6", "#ef4444", "#f97316", "#e11d48",
];

// Response Time / Resolution Time each get their own dropdown now instead
// of one shared filter-bar selector controlling both.
// Median dropped, Mean renamed to Average -- Rahim's original standing ask,
// re-confirmed 2026-08-15 after a mix-up earlier this session where Median
// was mistakenly left in place. Single source of truth: this array IS both
// the stored state value and the displayed label (see AGG_METHODS.map
// below), so getAggregationValue's switch (Min/Max explicit, everything
// else falls to its default/mean branch) needs no changes -- "Average"
// just falls through the same default case "Mean" used to.
const AGG_METHODS = ["Average", "Min", "Max"];

// Every column on the Overview table, for the "Customize columns" dialog
// (2026-08-12) -- listed in the same left-to-right order they appear in
// the table. UI ONLY for now (Rahim's explicit ask): checkboxes visually
// toggle but don't actually hide/show any column yet, all default checked.
const OVERVIEW_COLUMNS_LIST = [
  { key: "member", label: "Member" },
  { key: "inboxUsageTime", label: "Inbox usage time" },
  { key: "messagesSent", label: "Messages sent" },
  { key: "assigned", label: "Assigned" },
  { key: "closed", label: "Closed" },
  { key: "reassignCount", label: "Reassign Count" },
  { key: "responseTime", label: "Response Time" },
  { key: "resolutionTime", label: "Resolution Time" },
  { key: "firstResponseTime", label: "First Response Time" },
  { key: "responseCount", label: "Response Count" },
  { key: "resolutionCount", label: "Resolution Count" },
  { key: "firstResponseCount", label: "First Response Count" },
];

// Which OVERVIEW_COLUMNS_LIST keys really do render a dropdown-arrow icon
// in their real header (the 3 Time columns' Filter+Display-as popover, the
// 3 Count columns' Filter-only popover) -- used so the loading skeleton's
// header row can mirror that shape instead of looking identical for every
// column (2026-08-15).
// Wraps a header label in the shared "hover to learn what this means"
// tooltip (reportsInfoTooltipSx) -- looks up copy by the same key used in
// OVERVIEW_COLUMNS_LIST / CATEGORY_CONFIG so a header's tooltip and the
// category dropdown's own tooltip never say two different things for the
// same metric. `cursor: default` since the label itself isn't clickable
// (the sort/filter affordances are separate elements beside it).
const ReportsHeaderTooltip = ({ tKey, children }) => (
  <Tooltip title={REPORTS_TOOLTIP_COPY[tKey] || ""} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
    <Box component="span" sx={{ cursor: "default" }}>
      {children}
    </Box>
  </Tooltip>
);

const OVERVIEW_COLUMNS_WITH_DROPDOWN = [
  "responseTime",
  "resolutionTime",
  "firstResponseTime",
  "responseCount",
  "resolutionCount",
  "firstResponseCount",
];

// localStorage key for the Customize-columns choice (2026-08-15 -- was
// UI-only/in-memory before, now persists per-browser like most apps do for
// a personal display preference, not shared data).
const REPORTS_VISIBLE_COLUMNS_KEY = "clusterden_reports_visible_columns";

// Curated default visible set (Rahim's explicit call, 2026-08-15) -- the
// 3 newer/more niche columns (Inbox Usage Time, Resolution Count, First
// Response Count) start hidden so a first-time view isn't 12 columns wide;
// anyone who wants them just checks the box in Customize Columns. Member
// is always shown (not a real optional toggle, see below) so it's not
// listed here at all -- any key not in this set defaults to hidden.
const OVERVIEW_DEFAULT_HIDDEN_KEYS = ["inboxUsageTime", "resolutionCount", "firstResponseCount"];

const ReportsTable = ({ onCategoryModeChange }) => {
  const dispatch = useDispatch();
  const { reports, loading, error } = useSelector((state) => state.reports);
  const { userPermissions } = useSelector((state) => state.userDetails);
  const { activeMembers, teams } = useSelector((state) => state.settings);

  // Check if user has permission to view reports
  const canViewReports = userPermissions?.permissions?.Reports?.View || false;

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  // No more pagination on Overview (Rahim's ask, 2026-08-17) -- every
  // member renders at once, the table area scrolls vertically to reveal
  // the rest instead of paginating "N of M" pages. Replaces the whole
  // dynamic rowsPerPage-fitting system (measurement effect, ResizeObserver,
  // page state) that existed only to make pagination land on a clean page
  // size -- none of that's needed once there's no pagination to fit.
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Category drill-down (see categoryReportsConfig.js / CategoryReportDetail.jsx).
  // "overview" = today's per-member aggregate table (default, untouched).
  // Anything else swaps the SAME table Paper's content to that category's
  // flat event list -- see the render below.
  const [selectedCategory, setSelectedCategory] = useState("overview");
  // Reports.jsx's outer page wrapper needs to know when a category
  // drill-down is active so it can turn its own page-level scroll off (see
  // Reports.jsx's isCategoryMode comment) -- selectedCategory itself lives
  // here, not there, so this is a one-way signal up instead of lifting the
  // whole selection.
  useEffect(() => {
    onCategoryModeChange?.(selectedCategory !== "overview");
  }, [selectedCategory, onCategoryModeChange]);
  const [categoryMenuAnchorEl, setCategoryMenuAnchorEl] = useState(null);
  // Imperative handle into CategoryReportDetail's own exportCsv() -- the
  // Export button lives up here in the shared outer bar (Overview's own
  // spot), but the category table's current rows/columns/filters only
  // exist inside that child component, so a ref is the seam instead of
  // lifting all of its filter state up.
  const categoryDetailRef = useRef(null);
  // Category export now loops over every page server-side (see
  // CategoryReportDetail.jsx's exportCsv) instead of exporting only the
  // page on screen, so it can take a moment on a wide date range -- this
  // disables/relabels the shared Export button for the duration so a
  // second click can't fire an overlapping export.
  const [isCategoryExporting, setIsCategoryExporting] = useState(false);
  // Split from one shared "Mean" filter-bar dropdown into two independent
  // per-column choices — each column now has its own small dropdown icon in
  // its header instead of one selector controlling both at once.
  const [responseAggMethod, setResponseAggMethod] = useState("Average");
  const [resolutionAggMethod, setResolutionAggMethod] = useState("Average");
  const [responseAggAnchorEl, setResponseAggAnchorEl] = useState(null);
  const [resolutionAggAnchorEl, setResolutionAggAnchorEl] = useState(null);
  // New Overview columns (2026-08-12, Rahim's explicit correction: these
  // belong on THIS table, not the category drill-down). Values are now real
  // (Phase 1 + Phase 2, 2026-08-15). The header dropdown's Filter+Display-as
  // UI still doesn't dispatch a fetch on the operator/value inputs -- no
  // backend filtering wired for these columns yet, just real display values.
  const [firstResponseAggMethod, setFirstResponseAggMethod] = useState("Average");
  const [firstResponseAnchorEl, setFirstResponseAnchorEl] = useState(null);
  const [responseCountAnchorEl, setResponseCountAnchorEl] = useState(null);
  const [firstResponseTimeOperator, setFirstResponseTimeOperator] = useState(null);
  const [firstResponseTimeValue1, setFirstResponseTimeValue1] = useState("");
  const [firstResponseTimeValue2, setFirstResponseTimeValue2] = useState("");
  const [responseCountOperator, setResponseCountOperator] = useState(null);
  const [responseCountValue1, setResponseCountValue1] = useState("");
  const [responseCountValue2, setResponseCountValue2] = useState("");
  // Resolution Count / First Response Count -- new Overview columns
  // (2026-08-12), same "customize columns" candidates as Inbox Usage
  // Time/Reassign Count (see the 3-dot menu below). Same stale/mock +
  // working-dropdown-shell pattern as First Response Time/Response Count.
  const [resolutionCountAnchorEl, setResolutionCountAnchorEl] = useState(null);
  const [firstResponseCountAnchorEl, setFirstResponseCountAnchorEl] = useState(null);
  const [resolutionCountOperator, setResolutionCountOperator] = useState(null);
  const [resolutionCountValue1, setResolutionCountValue1] = useState("");
  const [resolutionCountValue2, setResolutionCountValue2] = useState("");
  const [firstResponseCountOperator, setFirstResponseCountOperator] = useState(null);
  const [firstResponseCountValue1, setFirstResponseCountValue1] = useState("");
  const [firstResponseCountValue2, setFirstResponseCountValue2] = useState("");
  // Customize columns dialog (see OVERVIEW_COLUMNS_LIST above) -- now
  // functional (2026-08-15, was UI-only): checkboxes actually hide/show the
  // matching table column and persist across reloads via localStorage.
  // Loads whatever was saved last, merged over defaults so a brand-new
  // column (added later) still shows up checked even if it wasn't in an
  // old saved value.
  const [customizeColumnsOpen, setCustomizeColumnsOpen] = useState(false);
  // 3-dot menu (2026-08-17, Rahim's explicit ask) -- was a direct shortcut
  // straight into the Customize Columns dialog; now reveals a small menu
  // first (room for more actions here later), and only that menu's own
  // "Customize columns" item opens the dialog.
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [visibleOptionalColumns, setVisibleOptionalColumns] = useState(() => {
    const defaults = Object.fromEntries(
      OVERVIEW_COLUMNS_LIST.map((c) => [c.key, !OVERVIEW_DEFAULT_HIDDEN_KEYS.includes(c.key)])
    );
    try {
      const saved = JSON.parse(localStorage.getItem(REPORTS_VISIBLE_COLUMNS_KEY));
      if (saved && typeof saved === "object") return { ...defaults, ...saved };
    } catch (e) {
      // Corrupt/missing localStorage value -- fall back to all-visible.
    }
    return defaults;
  });

  // Filter card state (Apply-gated — see wave5-reports-fix-plan/team-reports
  // memory: nothing here fires a request until Apply is clicked, so casual
  // clicking through members/duration inputs never touches the network or
  // the reports rate limit). selectedMembers holds full member objects (for
  // the Autocomplete's chip labels); ids are derived only when applying.
  const [selectedMembers, setSelectedMembers] = useState([]);
  // Each duration filter is an operator (">"/"<"/"between", or null = off)
  // plus one or two values, instead of bare min/max boxes — see
  // DurationOperatorFilter above.
  const [responseTimeOperator, setResponseTimeOperator] = useState(null);
  const [responseTimeValue1, setResponseTimeValue1] = useState("");
  const [responseTimeValue2, setResponseTimeValue2] = useState("");
  const [resolutionTimeOperator, setResolutionTimeOperator] = useState(null);
  const [resolutionTimeValue1, setResolutionTimeValue1] = useState("");
  const [resolutionTimeValue2, setResolutionTimeValue2] = useState("");

  useEffect(() => {
    dispatch(fetchActiveMembers());
    // Export-only need (2026-09-08): the Team export column below has to
    // know which team(s) each member belongs to. Not used anywhere on the
    // visible table -- Teams.members is a many-to-many array (a member can
    // sit in 0/multiple teams), so this never becomes an on-screen column.
    dispatch(fetchTeams());
  }, [dispatch]);

  // Runs once on mount: sets the default date range and kicks off the
  // permissions fetch. Deliberately does NOT depend on canViewReports (that
  // was the 2026-08-15 bug: canViewReports starts false before permissions
  // load, so this whole effect re-ran once it flipped true, firing
  // fetchUserPermissions() a second time on every single page load).
  useEffect(() => {
    // First day of the current month
    const firstDayOfMonth = new Date();
    firstDayOfMonth.setDate(1);
    firstDayOfMonth.setHours(0, 0, 0, 0);

    // Last day of the current month
    const lastDayOfMonth = new Date(
      firstDayOfMonth.getFullYear(),
      firstDayOfMonth.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  // Fires the one-time initial report fetch once BOTH the default dates are
  // set AND permission is confirmed -- whichever becomes true last triggers
  // it (order isn't guaranteed: canViewReports could already be true on
  // mount if permissions were cached from an earlier visit this session).
  // The ref guard is what makes this genuinely fire-once: dates are in the
  // dependency array so this effect legitimately re-runs on every date-range
  // change too, but hasFetchedInitialReportsRef stops it from ever
  // dispatching again after the first real fetch -- every later fetch goes
  // through the deliberate Apply-gated flow (handleApplyFilters etc.), not
  // this effect.
  const hasFetchedInitialReportsRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedInitialReportsRef.current && canViewReports && startDate && endDate) {
      hasFetchedInitialReportsRef.current = true;
      dispatch(fetchReports({ startTime: startDate, endTime: endDate }));
    }
  }, [dispatch, canViewReports, startDate, endDate]);

  useEffect(() => {
    if (reports) {
      setFilteredData(reports);
    }
  }, [reports]);

  // Overview's own bounded, internally-scrolling box (2026-09-05, "make the
  // header sticky" ask) -- Overview used to have no height cap at all and
  // relied on Reports.jsx's outer page wrapper to scroll (see that file's
  // history); that's what made position:sticky (Table's stickyHeader prop
  // below) unable to ever actually stick -- the box sticky binds to
  // (TableContainer, since it has overflowX:auto for the small-screen
  // horizontal fallback) never itself scrolled internally, so relative to
  // what you actually see scroll (the page), it wasn't sticky at all.
  // Rahim's explicit call: give this box a real internal vertical scroll
  // instead (all members still render at once, same as today -- just
  // reachable by scrolling THIS box, not the page), matching how every
  // major CRM table actually does it, and how CategoryReportDetail.jsx
  // already solves the same class of problem one component over.
  //
  // ceilingPx is measured the same way CategoryReportDetail does it
  // (comment there: "Deliberately NOT a ResizeObserver watching this
  // table's own container ... creates a feedback loop") -- but instead of
  // that file's hand-tuned per-breakpoint budget constants (tuned to ITS
  // own internal chrome, which Overview doesn't have -- Overview's filter
  // bar lives outside this Paper, not inside it), this reads
  // tableContainerRef's own getBoundingClientRect().top directly. That
  // number is a function of everything ABOVE this box (TopNav, filter bar,
  // Paper's own top padding) laid out by the browser -- not of this box's
  // own height -- so recomputing it doesn't feed back into itself the way
  // watching this box's own size would.
  const tableContainerRef = useRef(null);
  const [tableCeilingPx, setTableCeilingPx] = useState(null);
  useEffect(() => {
    if (selectedCategory !== "overview") return;
    const computeCeiling = () => {
      const top = tableContainerRef.current?.getBoundingClientRect().top;
      if (top === undefined) return;
      // Bottom breathing room -- matches Paper's own pb/mb roughly, so the
      // scroll box doesn't run flush to the viewport edge.
      const bottomBuffer = 32;
      const ceiling = window.innerHeight - top - bottomBuffer;
      // Sane floor so a transient mid-layout measurement never collapses
      // the box to near-0 (same guard class as CategoryReportDetail's own
      // "skip this pass rather than stick at a too-small value").
      setTableCeilingPx((prev) => {
        const next = Math.max(240, Math.round(ceiling));
        return prev === next ? prev : next;
      });
    };
    // Deferred one frame -- same fix as CategoryReportDetail's own measure
    // effect, avoids catching this mid-route-transition before layout settles.
    const rafId = requestAnimationFrame(computeCeiling);
    window.addEventListener("resize", computeCeiling);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", computeCeiling);
    };
  }, [selectedCategory, filteredData.length]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };


  // Date range selection now lives in ReportsDateRangeControl.jsx (shared
  // by the Overview filter bar and the category drill-down header).
  // Auto-applies immediately (2026-08-15 fix) -- the old "Apply"/"Reset"
  // bar buttons were removed in favor of Export a few sessions back, which
  // silently left date-range changes with no way to ever actually fetch:
  // this just staged startDate/endDate and nothing else was listening for
  // that change (only a column-header confirm-tick called
  // handleApplyFilters, which happened to also pick up whatever date was
  // staged at that moment -- so it looked like it sometimes worked,
  // depending on whether you'd also touched an unrelated filter). Selecting
  // a new range now dispatches immediately, passed as an override since
  // setStartDate/setEndDate haven't landed in state yet this same tick.
  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    handleApplyFilters({ payload: { startTime: newStartDate, endTime: newEndDate } });
  };

  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setCategoryMenuAnchorEl(null);
  };

  // Picking Average/Min/Max clears whatever operator filter (>/</between)
  // was active on this column -- the two are mutually exclusive (Rahim's
  // explicit rule, 2026-08-17): a column is either filtered by a value, or
  // displaying an aggregate, never both at once. Clearing the operator here
  // also feeds the debounced auto-apply effect above, so Response/
  // Resolution Time correctly re-fetch with the filter actually removed.
  const handleSelectResponseAgg = (method) => {
    setResponseAggMethod(method);
    setResponseTimeOperator(null);
    setResponseTimeValue1("");
    setResponseTimeValue2("");
    setResponseAggAnchorEl(null);
  };

  const handleSelectResolutionAgg = (method) => {
    setResolutionAggMethod(method);
    setResolutionTimeOperator(null);
    setResolutionTimeValue1("");
    setResolutionTimeValue2("");
    setResolutionAggAnchorEl(null);
  };

  const handleSelectFirstResponseAgg = (method) => {
    setFirstResponseAggMethod(method);
    setFirstResponseTimeOperator(null);
    setFirstResponseTimeValue1("");
    setFirstResponseTimeValue2("");
    setFirstResponseAnchorEl(null);
  };

  // Toggles + persists (see visibleOptionalColumns comment above).
  // "member" is locked ON -- it's the row identifier, hiding it would leave
  // a table of numbers nobody could attribute to anyone (Rahim's explicit
  // rule, 2026-08-17). Guarded here too, not just via the disabled checkbox
  // below, in case this ever gets called from somewhere else.
  const toggleOptionalColumn = (key) => {
    if (key === "member") return;
    setVisibleOptionalColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(REPORTS_VISIBLE_COLUMNS_KEY, JSON.stringify(next));
      } catch (e) {
        // localStorage unavailable (private mode/quota) -- still updates in-memory.
      }
      return next;
    });
  };

  // Column-header suffix showing the ACTIVE filter value inline (e.g.
  // "(>5m)"/"(<5m)"/"(5m to 10m)") instead of just the colored dropdown
  // arrow -- Rahim's explicit ask, 2026-08-12: the arrow alone doesn't say
  // what the filter actually is. `fallback` is what shows when no filter
  // is set (e.g. "(m)", the old static unit label).
  const columnFilterSuffix = (operator, value1, value2, unit, fallback) => {
    if (operator === "gt" && value1 !== "") return `(>${value1}${unit})`;
    if (operator === "lt" && value1 !== "") return `(<${value1}${unit})`;
    if (operator === "between" && (value1 !== "" || value2 !== "")) {
      return `(${value1 || 0}${unit} to ${value2 || "∞"}${unit})`;
    }
    return fallback;
  };

  // ONLY the date range is a real backend-required parameter now (2026-08-17,
  // Rahim's ask) -- memberIds and the response/resolution time min/max were
  // being sent on every fetch too, which (a) made the 2 duration filters
  // trigger a needless DB round-trip on their tick button (removed below,
  // see countFilteredData) and (b) meant that selecting members THEN
  // changing the date range would silently over-scope the query to just
  // those members, even though member-selection is documented elsewhere in
  // this file as a pure client-side row filter. Every column filter is now
  // client-side only, reusing the one unfiltered per-member fetch this page
  // already loads -- this function/dispatch exists solely for genuine
  // server-required changes (date range, explicit reset).
  const handleApplyFilters = (overrides = {}) => {
    const payload = { startTime: startDate, endTime: endDate, ...overrides.payload };
    // Defensive -- startTime/endTime are backend-required (server 400s
    // without them). A null date here means startDate/endDate genuinely
    // haven't loaded yet -- skip silently, the initial-fetch effect will
    // dispatch the real first request once they do.
    if (!payload.startTime || !payload.endTime) return;
    dispatch(fetchReports(payload));
  };

  // Response/Resolution/First Response Count, when a filter is set: counts
  // how many of THIS member's INDIVIDUAL events (a raw-seconds list, one
  // entry per reply/close/first-reply) were under/over the entered MINUTES
  // threshold -- not a total/average, an actual per-event count. Was a
  // single-number comparison against a plain aggregate count (Overview
  // already fetches every member's full row in one call, so this stays a
  // client-side filter, no backend round-trip) until 2026-08-16, when all
  // 3 switched from raw-count to minutes-based filtering, one at a time --
  // Response Count first, Resolution/First Response Count following the
  // same proven pattern right after. Core comparison shared here since all
  // 3 need the identical list-filtering logic, just with their own list +
  // operator/value state. Returns null when no operator is set, meaning
  // "not filtered" -- callers fall back to the raw total in that case
  // (e.g. row.messageStampsCount).
  const countTimesMatchingOperator = (times, operator, value1, value2) => {
    if (!operator) return null;
    const v1 = value1 !== "" ? parseFloat(value1) * 60 : undefined; // minutes -> seconds
    const v2 = value2 !== "" ? parseFloat(value2) * 60 : undefined;
    return (times || []).filter((t) => {
      if (operator === "gt") return v1 === undefined || t >= v1;
      if (operator === "lt") return v1 === undefined || t <= v1;
      if (operator === "between") {
        return (v1 === undefined || t >= v1) && (v2 === undefined || t <= v2);
      }
      return true;
    }).length;
  };

  const getQualifyingResponseCount = (row) =>
    countTimesMatchingOperator(row.responseTimesList, responseCountOperator, responseCountValue1, responseCountValue2);
  const getQualifyingResolutionCount = (row) =>
    countTimesMatchingOperator(row.resolutionTimesList, resolutionCountOperator, resolutionCountValue1, resolutionCountValue2);
  const getQualifyingFirstResponseCount = (row) =>
    countTimesMatchingOperator(row.firstResponseTimesList, firstResponseCountOperator, firstResponseCountValue1, firstResponseCountValue2);

  // Member selection (2026-08-17): every member's row is already in
  // filteredData from the one unfiltered fetch, so narrowing to selected
  // members is a pure client-side row filter, same as the Count filters
  // right above -- no backend call. String() on both sides since row.id
  // and m._id are Mongo ObjectIds (object identity won't match, string
  // value will).
  const selectedMemberIdSet = selectedMembers.length ? new Set(selectedMembers.map((m) => String(m._id))) : null;

  // Response/Resolution Time row exclusion (rebuilt 2026-08-17 -- no tick
  // button/backend fetch anymore, matches the Count filters exactly): the
  // single unfiltered fetch already returned every member's FULL
  // response/resolution/first-response data, so the operator is evaluated
  // right here client-side -- no dispatch, no round-trip, live on every
  // keystroke.
  const getAggregationValue = (row, field, method) => {
    switch (method) {
      case "Min":
        return row[`min${field}`] / 60;
      case "Max":
        return row[`max${field}`] / 60;
      default:
        return (
          row[`total${field}`] /
          (field === "ResolutionTime"
            ? row.resolutionTimeCount
            : field === "FirstResponseTime"
            ? row.firstResponseTimeCount
            : row.messagesWithResponse) /
          60
        );
    }
  };

  // Compares the SAME aggregate value the column actually displays (Average/
  // Min/Max, in minutes) against the operator+value -- not a per-event
  // check. Rebuilt 2026-08-17 after Rahim's bug report: a member whose
  // Average showed 1h8m still passed a "<5m" filter, because the first cut
  // of this reused Count's "any single qualifying event" logic (borrowed
  // from countTimesMatchingOperator), which doesn't match what the column
  // on-screen actually shows or what a duration filter reads as ("this
  // member's response time is under 5 minutes", not "at least one of their
  // replies happened to be fast"). NaN (no events at all for that metric,
  // e.g. 0/0) never matches any operator -- a member with nothing to
  // measure isn't "less than" or "more than" anything.
  const matchesTimeOperator = (valueMinutes, operator, value1, value2) => {
    if (Number.isNaN(valueMinutes)) return false;
    const v1 = value1 !== "" ? parseFloat(value1) : undefined;
    const v2 = value2 !== "" ? parseFloat(value2) : undefined;
    if (operator === "gt") return v1 === undefined || valueMinutes >= v1;
    if (operator === "lt") return v1 === undefined || valueMinutes <= v1;
    if (operator === "between") {
      return (v1 === undefined || valueMinutes >= v1) && (v2 === undefined || valueMinutes <= v2);
    }
    return true;
  };

  const countFilteredData = filteredData.filter(
    (row) =>
      (!selectedMemberIdSet || selectedMemberIdSet.has(String(row.id))) &&
      // Always compares against Average (Rahim's explicit call, 2026-08-17)
      // -- never whatever Min/Max was last picked before switching to a
      // filter. Hardcoded "Average" on purpose, not responseAggMethod/etc.
      (!responseTimeOperator ||
        matchesTimeOperator(getAggregationValue(row, "ResponseTime", "Average"), responseTimeOperator, responseTimeValue1, responseTimeValue2)) &&
      (!resolutionTimeOperator ||
        matchesTimeOperator(getAggregationValue(row, "ResolutionTime", "Average"), resolutionTimeOperator, resolutionTimeValue1, resolutionTimeValue2)) &&
      (!firstResponseTimeOperator ||
        matchesTimeOperator(getAggregationValue(row, "FirstResponseTime", "Average"), firstResponseTimeOperator, firstResponseTimeValue1, firstResponseTimeValue2)) &&
      (!responseCountOperator || getQualifyingResponseCount(row) > 0) &&
      (!resolutionCountOperator || getQualifyingResolutionCount(row) > 0) &&
      (!firstResponseCountOperator || getQualifyingFirstResponseCount(row) > 0)
  );

  const sortedData = [...countFilteredData].sort((a, b) => {
    if (orderBy === "name") {
      return order === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    return order === "asc" ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
  });

  // memberId -> ["Sales", "HR", ...] -- built once per teams fetch/refetch.
  // A member can belong to more than one team (Teams.members is an array,
  // TeamMember itself has no back-reference), so this is a 1-to-many map,
  // not a simple id->name lookup.
  const memberIdToTeamNames = useMemo(() => {
    const map = {};
    (teams || []).forEach((team) => {
      (team.members || []).forEach((member) => {
        const memberId = String(member?._id || member);
        if (!map[memberId]) map[memberId] = [];
        map[memberId].push(team.teamName);
      });
    });
    return map;
  }, [teams]);

  const getRandomAvatarColor = (name) => {
    // Use a hash of the whole name for more unique color assignment
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % avatarColors.length;
    return avatarColors[index];
  };

  // Response Count / Resolution Count -- (2026-08-15, Phase 1) wired to
  // real data (row.messageStampsCount/row.resolutionTimeCount, already in
  // the API response and already used elsewhere on this row for Messages
  // Sent / Resolution Time) -- the old getMockResponseCount/
  // getMockResolutionCount hash generators are gone, no longer referenced
  // anywhere in this file.

  // First Response Time / First Response Count -- (2026-08-15, Phase 2)
  // also wired to real data (row.totalFirstResponseTime/minFirstResponseTime/
  // maxFirstResponseTime/medianFirstResponseTime/firstResponseTimeCount, all
  // now returned by combinedReports.service.js). getMockFirstResponseTime/
  // getMockFirstResponseCount hash generators are gone.

  // Cascades all the way up (2026-08-15 -- Rahim's ask, extending the
  // minutes->hours fix into a full ladder): minutes/seconds under an hour,
  // hours/minutes under a day, days/hours under a week, weeks/days beyond
  // that. Each step only shows once the value's actually big enough to
  // need it -- a 90-minute value still reads as "1h 30m", not "0w 0d 1h
  // 30m". Used for both Inbox Usage Time (already fed seconds) and,
  // through formatMinutesAndSeconds below, the 3 Average Time columns.
  const formatHoursAndMinutes = (seconds) => {
    const totalMinutes = Math.floor(seconds / 60);
    const weeks = Math.floor(totalMinutes / (60 * 24 * 7));
    const days = Math.floor((totalMinutes % (60 * 24 * 7)) / (60 * 24));
    const hrs = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins = totalMinutes % 60;

    if (weeks > 0) {
      let result = `${weeks}w`;
      if (days > 0) result += ` ${days}d`;
      return result;
    }
    if (days > 0) {
      let result = `${days}d`;
      if (hrs > 0) result += ` ${hrs}h`;
      return result;
    }
    let result = '';
    if (hrs > 0) result += `${hrs}h`;
    if (mins > 0) {
      if (result) result += ' ';
      result += `${mins}m`;
    }
    return result || '0m';
  };

  // Switches to hours+minutes once the value is an hour or more (2026-08-15
  // fix -- was always "Xm Ys" no matter how large, so a real value like
  // 11237 minutes printed as the unreadable "11237m 43s" instead of the
  // correct "187h 17m"). Seconds only make sense to show at the
  // sub-hour scale, same reasoning formatHoursAndMinutes above already
  // applies to Inbox Usage Time -- reused here instead of a 3rd formatter.
  const formatMinutesAndSeconds = (minutes) => {
    if (minutes >= 60) {
      return formatHoursAndMinutes(minutes * 60);
    }
    const totalSeconds = Math.round(minutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    let result = '';
    if (mins > 0) result += `${mins}m`;
    if (secs > 0) {
      if (result) result += ' ';
      result += `${secs}s`;
    }
    return result || '0s';
  };

  // CSV export (2026-08-15) -- Overview mode only, category drill-down has
  // its own export inside CategoryReportDetail (different rows/columns
  // shape). Exports exactly what's on screen: only the columns currently
  // checked in Customize Columns, and every row in `sortedData` (the full
  // filtered+sorted set, not just the current page -- respects whatever
  // date range/member/duration filters are currently applied, same data
  // the table itself is built from).
  const getOverviewColumnValue = (key, row) => {
    switch (key) {
      case "member":
        return row.name;
      case "inboxUsageTime":
        return formatHoursAndMinutes(row.totalLoginDuration);
      case "messagesSent":
        return row.messageStampsCount;
      case "assigned":
        return row.assignedCount;
      case "closed":
        return row.closedCount;
      case "reassignCount":
        return row.reassignStampsCount;
      case "responseTime":
        return row.messagesWithResponse
          ? formatMinutesAndSeconds(getAggregationValue(row, "ResponseTime", responseAggMethod))
          : "N/A";
      case "resolutionTime":
        return row.resolutionTimeCount
          ? formatMinutesAndSeconds(getAggregationValue(row, "ResolutionTime", resolutionAggMethod))
          : "N/A";
      case "firstResponseTime":
        return row.firstResponseTimeCount
          ? formatMinutesAndSeconds(getAggregationValue(row, "FirstResponseTime", firstResponseAggMethod))
          : "N/A";
      case "responseCount":
        return responseCountOperator ? getQualifyingResponseCount(row) : row.messageStampsCount;
      case "resolutionCount":
        return resolutionCountOperator ? getQualifyingResolutionCount(row) : row.resolutionTimeCount;
      case "firstResponseCount":
        return firstResponseCountOperator ? getQualifyingFirstResponseCount(row) : row.firstResponseTimeCount;
      default:
        return "";
    }
  };

  const handleExportOverview = () => {
    const visibleColumns = OVERVIEW_COLUMNS_LIST.filter((c) => visibleOptionalColumns[c.key]);
    // "Team" is export-only (not gated by Customize Columns / not rendered
    // on the live table, see memberIdToTeamNames above) -- leads the row,
    // ahead of Member, matching the Zendesk/HubSpot convention of putting
    // the broad category column before the identity column. A member in
    // multiple teams gets one cell with every team name comma-separated
    // (same tools' own convention for a multi-value field on export: one
    // row per record, never a duplicated row per value).
    const headers = ["Team", ...visibleColumns.map((c) => c.label)];
    const rows = sortedData.map((row) => [
      (memberIdToTeamNames[String(row.id)] || []).join(", "),
      ...visibleColumns.map((c) => getOverviewColumnValue(c.key, row)),
    ]);
    downloadCsv(`reports-overview-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  // If user doesn't have permission to view reports
  if (!canViewReports) {
    return <AuthView />;
  }

  if (loading) {
    // Display skeleton rows while loading
    return (
      <>
        {/* Lightweight placeholder matching the real filter card's shape/height
            — the real card (with its live state) would flash empty controls
            for a moment otherwise, so a plain skeleton bar reads better here.
            Sits above the table Paper, same as the real filter bar below. */}
        <Box
          sx={{
            ...reportsFilterCardSx,
            gap: 1.25,
            // Matches the real filter bar's width below (2026-09-05: widened
            // back out per client feedback wanting more columns visible at
            // once — was capped at 1150/1300px 2026-08-17, reversed now).
            // Kept in sync so the skeleton doesn't visibly narrow/jump the
            // instant real data loads in.
            maxWidth: "100%",
          }}
        >
          <Skeleton variant="rounded" width={190} height={40} sx={{ bgcolor: "rgba(127,127,127,0.12)" }} />
          <Skeleton variant="rounded" width={220} height={40} sx={{ bgcolor: "rgba(127,127,127,0.12)" }} />
          <Skeleton variant="rounded" width={180} height={40} sx={{ bgcolor: "rgba(127,127,127,0.12)" }} />
          <Skeleton variant="rounded" width={180} height={40} sx={{ bgcolor: "rgba(127,127,127,0.12)" }} />
          <Box sx={{ ml: { lg: "auto" } }}>
            <Skeleton variant="rounded" width={110} height={40} sx={{ bgcolor: "rgba(127,127,127,0.12)" }} />
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            // Matches the real table Paper's exact box below -- kept in sync
            // so the skeleton doesn't visibly jump width the instant data
            // arrives (see that Paper's own comment for the 2026-09-05
            // width-increase history).
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            px: { xs: 1.5, sm: 2, md: 2, lg: 2.5, xl: 3 },
            pt: { xs: 0.75, sm: 1, md: 1, lg: 1, xl: 1.25 },
            pb: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: (theme) => taskTableOuterBorder(theme),
            boxShadow: "none",
            color: "text.primary",
          }}
        >
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {/* Was a bare Skeleton with no TableCell wrapper -- invalid
                      HTML (a <span> can't sit directly inside <tr>), threw a
                      validateDOMNesting console warning on every load.
                      Mirrors the real header: one cell per currently-visible
                      column (visibleOptionalColumns), and (2026-08-15) a
                      small circular placeholder next to the text for every
                      column that really does have a dropdown-arrow icon
                      (Time/Count columns) -- the plain bar alone didn't
                      hint that those cells are wider/interactive.
                      2026-09-04: every bar/circle below was hardcoded to a
                      white-based rgba (255,255,255,0.1) -- correct for the
                      table's own dark-mode toggle but literally invisible
                      against light mode's white Paper (confirmed live,
                      screenshot showed blank rows with no visible skeleton
                      at all), which is what read as "distorted." Now
                      theme-aware, matching the light-mode color the app's
                      other skeletonSx convention already uses. */}
                  {OVERVIEW_COLUMNS_LIST.filter((c) => visibleOptionalColumns[c.key]).map((c) => (
                    <TableCell key={c.key}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Skeleton width="60%" sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)") }} />
                        {OVERVIEW_COLUMNS_WITH_DROPDOWN.includes(c.key) && (
                          <Skeleton variant="circular" width={16} height={16} sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)") }} />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* A small fixed count -- just signals "a table is loading"
                    without implying a specific row count (2026-08-15). */}
                {[...Array(4)].map((_, index) => (
                  <TableRow key={index}>
                    {OVERVIEW_COLUMNS_LIST.filter((c) => visibleOptionalColumns[c.key]).map((c) => (
                      <TableCell key={c.key}>
                        {c.key === "member" ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Skeleton
                              variant="circular"
                              width={32}
                              height={32}
                              sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)") }}
                            />
                            <Skeleton width={90} sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)") }} />
                          </Box>
                        ) : (
                          <Skeleton width="50%" sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)") }} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </>
    );
  }

  if (error) return <Typography color="error.main">Error: {error}</Typography>;

  /** Scrollbars on Paper (horizontal) + TableContainer (vertical/inner horizontal): WebKit + Firefox */
  const reportsTableScrollbarSx = {
    scrollbarWidth: "thin",
    scrollbarColor: (t) =>
      t.palette.mode === "dark"
        ? `${t.palette.grey[600]} ${t.palette.grey[900]}`
        : `${t.palette.grey[500]} ${t.palette.grey[100]}`,
    "&::-webkit-scrollbar": {
      width: { xs: "6px", sm: "6px", md: "6px", lg: "8px", xl: "8px" },
      height: { xs: "6px", sm: "6px", md: "6px", lg: "8px", xl: "8px" },
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: (t) =>
        t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100],
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: (t) =>
        t.palette.mode === "dark" ? t.palette.grey[600] : t.palette.grey[400],
      borderRadius: "8px",
      border: (t) =>
        t.palette.mode === "dark"
          ? `2px solid ${t.palette.grey[900]}`
          : `2px solid ${t.palette.grey[100]}`,
      backgroundClip: "padding-box",
      "&:hover": {
        backgroundColor: (t) =>
          t.palette.mode === "dark" ? t.palette.grey[500] : t.palette.grey[500],
      },
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: (t) =>
        t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100],
    },
  };

  return (
    <>
      {/* Filter bar now sits above the table container instead of inside it
          — was reading as glued to the table Paper, taken out per Rahim's
          screenshot callout. Chips + custom-range popover moved out with it
          since they're part of the same filter group, not the table. */}
      <Box
        sx={{
          ...reportsFilterCardSx,
          // Widened back to full width (2026-09-05, client feedback: wants
          // to see all the table's columns in one view without scrolling
          // on a normal-size screen). Was narrowed+centered to 1150/1300px
          // on 2026-08-17 for a spacing preference at the time ("was
          // reading as edge-to-edge white space") -- reversed now that
          // fitting more columns matters more. Applies to both Overview and
          // category drill-down (same bar, same card). xs/sm/md were
          // always 100%, untouched -- this only affects lg/xl.
        }}
      >
        {/* Date range -- Overview mode only. In category mode the SAME
            control renders inside CategoryReportDetail's header instead
            (never both at once -- one shared date-range state, one place
            it's rendered at a time). */}
        {selectedCategory === "overview" && (
          <ReportsDateRangeControl
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleDateRangeChange}
          />
        )}

        {/* Back -- only shows once a category is selected, sits right
            beside the Category pill (Rahim's explicit placement, 2026-08-12:
            not inside the box next to the title). "Overview" is NOT a menu
            item below -- this Back button is the one way back to it. */}
        {selectedCategory !== "overview" && (
          <IconButton
            size="small"
            onClick={() => handleCategorySelect("overview")}
            sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: "8px", height: REPORTS_PILL_HEIGHT, width: REPORTS_PILL_HEIGHT, color: "text.secondary" }}
          >
            <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BackIcon size="100%" color="currentColor" />
            </Box>
          </IconButton>
        )}

        {/* Category picker -- always visible, the mode switch between
            Overview (today's per-member table) and a drill-down category
            (flat event list, see CategoryReportDetail.jsx). Options are
            generated from CATEGORY_CONFIG, so adding a category later never
            touches this JSX. */}
        <Button
          variant="outlined"
          onClick={(e) => setCategoryMenuAnchorEl(e.currentTarget)}
          size="small"
          endIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>}
          sx={reportsFilterPillSx}
        >
          {selectedCategory === "overview" ? "Select Category" : CATEGORY_CONFIG[selectedCategory]?.label}
        </Button>
        <Menu
          anchorEl={categoryMenuAnchorEl}
          open={Boolean(categoryMenuAnchorEl)}
          onClose={() => setCategoryMenuAnchorEl(null)}
          slotProps={{
            paper: {
              sx: { mt: 0.75, ...reportsDropdownPaperSx },
            },
          }}
        >
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <MenuItem
              key={key}
              selected={selectedCategory === key}
              onClick={() => handleCategorySelect(key)}
              sx={{
                fontSize: "0.9rem",
                fontWeight: selectedCategory === key ? 600 : 400,
                py: 0.95,
                px: 1.625,
                mb: 0.25,
                borderRadius: "8px",
                mx: 0.5,
              }}
            >
              {config.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Members multi-select — Overview mode only (see reports-page-heading-pattern
            memory / team-reports session 2026-08-12: in category mode,
            member-scoping happens via that category's own "+ Add filter"
            chips instead, so a persistent global Members dropdown here
            would just be a dead control). Options come straight from the
            account's active member list (fetchActiveMembers), a request
            already fired once on mount, so no extra call is needed just to
            populate this. */}
        {selectedCategory === "overview" && (
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          // Matches "Select Category"'s chevron exactly (Rahim, 2026-08-17)
          // -- MUI's default Autocomplete popupIcon (ArrowDropDownIcon) is a
          // different, heavier triangle shape than iconsax's ArrowDown2,
          // used everywhere else in this filter bar (2026-09-04: swapped
          // from MUI's KeyboardArrowDownIcon to match the AI Agents pass).
          popupIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>}
          options={activeMembers || []}
          value={selectedMembers}
          onChange={(_, newValue) => {
            // No backend round-trip (2026-08-17, Rahim's explicit ask) --
            // the initial unfiltered fetch already returns every member's
            // row, fully computed, so narrowing to selected members is
            // just hiding rows already sitting in memory. See
            // countFilteredData below for the actual filtering step.
            // Duration filters (Response/Resolution Time) still correctly
            // hit the backend on their own Apply -- unaffected, they
            // change which EVENTS count into each member's numbers, which
            // this data doesn't have client-side to recompute.
            setSelectedMembers(newValue);
          }}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          limitTags={1}
          // Custom tag rendering (2026-08-17, Rahim's explicit ask) -- MUI's
          // default Chip tags read as cramped/unstyled inside this pill's
          // fixed height, and the "+1" overflow indicator was plain
          // unstyled text sitting awkwardly next to the first tag. Now a
          // small rounded pill matching the app's soft-tint chip
          // convention, and the "+N" badge gets the same pill treatment
          // instead of bare text.
          renderTags={(value, getTagProps) => {
            const visible = value.slice(0, 1);
            const overflowCount = value.length - visible.length;
            return (
              <>
                {visible.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      size="small"
                      label={option.name}
                      sx={{
                        height: 22,
                        fontSize: "0.78rem",
                        borderRadius: "6px",
                        backgroundColor: "action.selected",
                        "& .MuiChip-deleteIcon": { fontSize: 14, margin: "0 2px 0 -4px" },
                      }}
                    />
                  );
                })}
                {overflowCount > 0 && (
                  <Chip
                    size="small"
                    label={`+${overflowCount}`}
                    sx={{
                      height: 22,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      borderRadius: "6px",
                      backgroundColor: "action.selected",
                      color: "text.secondary",
                    }}
                  />
                )}
              </>
            );
          }}
          sx={{
            minWidth: { xs: "100%", lg: 220 },
            flex: { lg: "0 1 250px" },
            // Same rounded-corner pill radius, height, and font size as the
            // Date Range button next to it (REPORTS_PILL_HEIGHT/FONT_SIZE
            // above) -- MUI's own Autocomplete/TextField padding defaults
            // to a taller box than a plain Button, which is exactly why
            // they looked mismatched before. Explicit white background now
            // that there's no outer card providing one.
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "background.paper",
              height: REPORTS_PILL_HEIGHT,
              fontSize: REPORTS_PILL_FONT_SIZE,
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              paddingLeft: "14px !important",
              // Was never set -- fell back to MUI's own default outline
              // color instead of the app's divider token, which reads
              // fine in light mode (close enough by coincidence) but is
              // visibly a different, much fainter grey in dark mode next
              // to the Select Category/Date Range pills. Matches those
              // pills' border exactly now (reportsFilterPillSx).
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => theme.palette.divider,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "text.primary",
              },
            },
            "& .MuiOutlinedInput-input": {
              fontSize: REPORTS_PILL_FONT_SIZE,
              padding: "0 !important",
            },
            // The tag/chip row and the popup arrow have their own MUI
            // internal wrappers, independent of MuiOutlinedInput-root above
            // -- centering the outer box alone wasn't enough. inputRoot
            // controls the icon+chips+input row; endAdornment (the dropdown
            // arrow) is absolutely positioned by MUI assuming a shorter
            // default height, which reads off-center against this pill's
            // taller custom height -- pinned to true vertical center
            // explicitly instead of MUI's calc() assumption.
            "& .MuiAutocomplete-inputRoot": {
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
            },
            "& .MuiAutocomplete-endAdornment": {
              top: "50% !important",
              transform: "translateY(-50%)",
            },
          }}
          slotProps={{
            paper: {
              sx: { mt: 0.75, ...reportsDropdownPaperSx },
            },
          }}
          renderOption={(props, option, { selected }) => (
            <Box
              component="li"
              {...props}
              sx={{
                fontSize: "0.9rem",
                py: "7.6px !important",
                px: "13px !important",
                mb: "2px",
                mx: "4px",
                borderRadius: "8px",
                width: "calc(100% - 8px)",
                // No colored outline/border on focus or selection -- was
                // showing MUI's default theme-primary (green) focus ring,
                // which read as an unintended accent border. Plain grey
                // hover/highlight only, matching the other 2 dropdowns.
                outline: "none",
                border: "none",
                "&:focus, &:focus-visible, &.Mui-focused, &.Mui-focusVisible": {
                  outline: "none",
                  border: "none",
                  backgroundColor: "action.hover",
                },
                '&[aria-selected="true"]': reportsDropdownSelectedSx,
              }}
            >
              <Checkbox
                icon={checkboxUncheckedIcon}
                checkedIcon={checkboxCheckedIcon}
                checked={selected}
                size="small"
                // Kept neutral grey, not the theme's green primary --
                // selection here shouldn't read as a colored/brand accent.
                sx={{ mr: 0.75, p: 0.25, color: "text.secondary", "&.Mui-checked": { color: "text.secondary" } }}
              />
              {option.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedMembers.length ? "" : "All members"}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", mr: 0.5, ml: 0.25 }}>
                      <Profile2User size="100%" color="currentColor" />
                    </Box>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        )}

        {/* Export, right-aligned -- shown in BOTH Overview and category
            mode now (2026-08-13: previously Overview-only; category's own
            copy inside CategoryReportDetail's header was removed in favor
            of this single shared spot, same top-right position either way).
            Replaces the old Reset/Apply pair for this UI/UX pass. Export
            itself is a UI placeholder for now, not wired to a real export
            yet -- pure
            frontend pass, no backend touched.
            White bg + black text (not the green filled look), and reuses
            the EXACT same pill base (reportsFilterPillSx) as the Date
            Range/Members controls -- was "contained" (zero border) before,
            a different box model than their "outlined" (1px border-box),
            which is what threw its vertical alignment off next to them. */}
        <Box sx={{ ml: { lg: "auto" }, flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
          {/* Customize columns -- 2026-08-12, sits LEFT of Export (Rahim's
              explicit placement), white/paper background matching every
              other pill, opens as a CENTERED dialog (not an anchored
              dropdown) listing every column on this table
              (OVERVIEW_COLUMNS_LIST). Overview mode ONLY -- column
              customization doesn't apply to the category drill-down's
              config-driven columns. Functional (2026-08-15): checkboxes
              actually hide/show the matching column and persist via
              localStorage (visibleOptionalColumns). */}
          {selectedCategory === "overview" && (
            <>
              <IconButton
                size="small"
                onClick={(e) => setMoreMenuAnchorEl(e.currentTarget)}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  height: REPORTS_PILL_HEIGHT,
                  width: REPORTS_PILL_HEIGHT,
                  color: "text.secondary",
                  bgcolor: "background.paper",
                  "&:hover": { borderColor: "text.primary", bgcolor: "background.paper" },
                }}
              >
                <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MoreIcon size="100%" color="currentColor" />
                </Box>
              </IconButton>
              <Menu
                anchorEl={moreMenuAnchorEl}
                open={Boolean(moreMenuAnchorEl)}
                onClose={() => setMoreMenuAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: { mt: 0.75, ...reportsDropdownPaperSx },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setCustomizeColumnsOpen(true);
                    setMoreMenuAnchorEl(null);
                  }}
                  sx={{ fontSize: "0.9rem", py: 0.95, px: 1.625, mb: 0.25, borderRadius: "8px", mx: 0.5, gap: 1 }}
                >
                  <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
                    <CustomizeColumnsIcon size="100%" color="currentColor" />
                  </Box>
                  Customize columns
                </MenuItem>
              </Menu>
              <Dialog
                open={customizeColumnsOpen}
                onClose={() => setCustomizeColumnsOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "16px",
                      boxShadow: (theme) => (theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.12)"),
                    },
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    pt: 2.5,
                    px: 2.75,
                    pb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  Customize columns
                  <IconButton
                    size="small"
                    onClick={() => setCustomizeColumnsOpen(false)}
                    sx={{ color: "text.secondary", ml: 1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: 1.5, pb: 1.5 }}>
                  {OVERVIEW_COLUMNS_LIST.map((col) => (
                    <FormControlLabel
                      key={col.key}
                      disabled={col.key === "member"}
                      control={
                        <Checkbox
                          size="small"
                          checked={visibleOptionalColumns[col.key]}
                          disabled={col.key === "member"}
                          onChange={() => toggleOptionalColumn(col.key)}
                          // Tick icon from iconsax (our standard icon
                          // library, 2026-08-17) for the CHECKED state only
                          // -- TickSquare's "Linear" (outline) variant still
                          // draws a faint checkmark even when meant to read
                          // as unchecked (it's not a blank square, just a
                          // thinner tick), so the unchecked state uses a
                          // genuinely empty bordered square instead of that
                          // icon at all.
                          icon={
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "5px",
                                border: (theme) => `1.5px solid ${theme.palette.divider}`,
                              }}
                            />
                          }
                          checkedIcon={
                            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", color: "primary.main" }}>
                              <TickSquare variant="Bold" size="100%" color="currentColor" />
                            </Box>
                          }
                          sx={{ color: "text.secondary" }}
                        />
                      }
                      label={col.label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        ml: 0,
                        gap: 1,
                        py: 0.4,
                        px: 1.25,
                        mb: 0.1,
                        mx: 0,
                        borderRadius: "8px",
                        "&:hover": { backgroundColor: "action.hover" },
                        "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
                      }}
                    />
                  ))}
                </DialogContent>
              </Dialog>
            </>
          )}

          <ReportsExportButton
            loading={selectedCategory !== "overview" && isCategoryExporting}
            onExportCsv={
              selectedCategory === "overview"
                ? handleExportOverview
                : () => categoryDetailRef.current?.exportCsv()
            }
          />
        </Box>
      </Box>

      {/* Active-filter chip row removed (2026-08-17) -- it read from
          appliedFilterSummary.responseTimeMinInput/resolutionTimeMinInput,
          property names that were never actually set (the object only ever
          stored responseTimeValue1/resolutionTimeValue1 etc.), so this block
          was permanently dead/never rendered. Now that Response/Resolution
          Time are instant client-side filters (no Apply step, see
          countFilteredData above), there's no separate "applied vs draft"
          state left to summarize anyway -- the column header's own inline
          suffix (columnFilterSuffix) is the one active-filter indicator,
          same as every Count column already relies on with no chip. Clearing
          a filter is done the same way Count's is: reopen the dropdown and
          click the active operator button again (ToggleButtonGroup
          deselects on re-click) or pick Average/Min/Max. */}

      <Paper
        // Bordered card instead of a shadow-elevated one, matching the Tasks
        // table's own container treatment (elevation 0 + explicit border).
        elevation={0}
        sx={{
          width: "100%",
          // Widened back to full width (2026-09-05, client feedback: wants
          // to see all the table's columns in one view without scrolling
          // on a normal-size screen). Was narrowed+centered to 1150/1300px
          // on 2026-08-17 for a spacing preference at the time ("was
          // reading as edge-to-edge white space") -- reversed now that
          // fitting more columns matters more. Matches the filter bar
          // above so both line up; applies to both Overview and category
          // drill-down (same Paper). overflowX below is the untouched
          // responsive fallback -- a genuinely small/narrow viewport still
          // scrolls the table horizontally instead of squeezing columns.
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "auto",
          overflowY: "hidden",
          // Horizontal stayed roomy; vertical pulled back down — the flat
          // padding was leaving a lot of dead space above the header and
          // (worse, since the pagination toolbar adds its own height on
          // top of it) below the pagination row, for a table that's often
          // just 1-2 rows tall.
          px: { xs: 1.5, sm: 2, md: 2, lg: 2.5, xl: 3 },
          // Split top/bottom -- the card's own bottom padding was much
          // tighter (6-10px) than the gap above the card between the filter
          // bar and the card (16-20px, reportsFilterCardSx's mb). Matched
          // pb to that same scale so both gaps read as equal (Rahim's ask,
          // 2026-08-17); pt stays as it was.
          pt: { xs: 0.75, sm: 1, md: 1, lg: 1, xl: 1.25 },
          pb: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
          // Explicit responsive gap below the card (Rahim's ask, 2026-08-17)
          // -- decoupled from the height calc above so there's always
          // visible breathing room before the viewport edge, regardless of
          // how well-tuned that calc is at any given screen size.
          mb: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 4 },
          backgroundColor: "background.paper",
          borderRadius: "12px",
          border: (theme) => taskTableOuterBorder(theme),
          boxShadow: "none",
          color: "text.primary",
          ...reportsTableScrollbarSx,
        }}
      >
        {selectedCategory !== "overview" ? (
          // Category drill-down -- same Paper, content swapped in place
          // (no popup/drawer, per Rahim's explicit call). Real data from
          // the /reports/category endpoint; columns from CATEGORY_CONFIG.
          <CategoryReportDetail
            ref={categoryDetailRef}
            category={selectedCategory}
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleDateRangeChange}
            members={activeMembers}
            onExportingChange={setIsCategoryExporting}
          />
        ) : (
        <>
        <TableContainer
          ref={tableContainerRef}
          sx={{
            backgroundColor: "background.paper",
            // Real bounded height + real internal vertical scroll
            // (2026-09-05, sticky-header ask, reversing the 2026-08-17
            // "whole page scrolls" call -- Rahim's explicit go-ahead: "i
            // dont mind the parent page scroll bar to disapear"). All
            // members still render at once (no pagination, per Rahim's
            // explicit confirmation) -- just reachable by scrolling THIS
            // box now instead of the page. tableCeilingPx is measured
            // above; the calc() is only a same-frame fallback before that
            // first measurement lands. `stickyHeader` (Table prop below)
            // now sticks correctly since this box is a REAL scrolling
            // ancestor (not the fake unbounded-but-"scroll-container"
            // box the old overflowY:"visible" version left behind).
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: tableCeilingPx ? `${tableCeilingPx}px` : "calc(100vh - 320px)",
            width: "100%",
            marginTop: { xs: 0.5, sm: 1, md: 1, lg: 1, xl: 1 },
            scrollbarWidth: "thin",
            scrollbarColor: (t) =>
              t.palette.mode === "dark"
                ? `${t.palette.grey[600]} ${t.palette.grey[900]}`
                : `${t.palette.grey[500]} ${t.palette.grey[100]}`,
            "&::-webkit-scrollbar": {
              width: { xs: "4px", sm: "4px", md: "4px", lg: "6px", xl: "6px" },
              height: { xs: "4px", sm: "4px", md: "4px", lg: "6px", xl: "6px" },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100],
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[600] : t.palette.grey[400],
              borderRadius: "8px",
              border: (t) =>
                t.palette.mode === "dark"
                  ? `2px solid ${t.palette.grey[900]}`
                  : `2px solid ${t.palette.grey[100]}`,
              backgroundClip: "padding-box",
              "&:hover": {
                backgroundColor: (t) =>
                  t.palette.mode === "dark" ? t.palette.grey[500] : t.palette.grey[500],
              },
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100],
            },
            // Body stays 14px (matches the app's Body token). Header is one
            // step down at 13px (Small/Label token, same as Inbox/Dashboard's
            // own table-header convention) -- weight (600 vs 400) still does
            // the differentiating, just off a slightly smaller header size
            // now instead of matching body exactly (2026-09-04, to bring
            // Reports off its own oversized "Team performance"-reference
            // scale onto the same one Inbox/AI Agents use). Padding stays
            // responsive (tight on mobile so the table doesn't crowd/scroll
            // more than it has to, roomy on desktop) -- font itself stays
            // flat per font-sizing-scale.md's rule. Background + alignment
            // below matches the Tasks table (Rahim's explicit reference),
            // reusing its own color values.
            "& .MuiTableCell-root": {
              fontSize: "14px",
              fontWeight: 400,
              // Tightened a step (2026-09-04, "reduce the spacing a
              // little" ask) -- was 10-18px both directions. Horizontal
              // widened again at lg/xl only (2026-09-05, "increase the
              // space between the vertical columns" for big screens) --
              // xs/sm/md keep the exact tight values from that earlier
              // pass; vertical padding untouched at every size.
              // Horizontal tightened further at lg only (2026-09-05, "fit
              // all columns without horizontal scroll" ask) -- lg is a
              // ~1440px laptop width, where the fixed LeftNav rail +
              // ReportsSidebar (~300px combined) leaves a genuinely tight
              // ~1085px for 10 columns; every reclaimed horizontal px here
              // counts. xs/sm/md/xl untouched.
              padding: { xs: "8px 10px", sm: "10px 12px", md: "11px 13px", lg: "10px 8px", xl: "13px 14px" },
              whiteSpace: "nowrap",
              verticalAlign: "middle",
              backgroundColor: "background.paper",
            },
            // Header now matches Knowledge Sources' own header cell exactly
            // (2026-09-04, "use the same header as we did for knowledge
            // sources" ask) -- same fontSize/color/tint/border shape as
            // KnowledgeSourcesTab.jsx's TableHead TableCell, not just a
            // close approximation of the tint alone (the earlier pass here
            // matched the bgcolor but kept Reports' own no-border/wrapped
            // header instead of KB's plain single-line bottom-border row).
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontSize: "13px",
              // Tightened a step (2026-09-04, "reduce the spacing a
              // little" ask) -- was 10-18px both directions. Horizontal
              // widened again at lg/xl only (2026-09-05, "increase the
              // space between the vertical columns" for big screens) --
              // must match the body cell rule above exactly (same values)
              // or header/body columns stop lining up.
              // Horizontal tightened further at lg only (2026-09-05, "fit
              // all columns without horizontal scroll" ask) -- lg is a
              // ~1440px laptop width, where the fixed LeftNav rail +
              // ReportsSidebar (~300px combined) leaves a genuinely tight
              // ~1085px for 10 columns; every reclaimed horizontal px here
              // counts. xs/sm/md/xl untouched.
              padding: { xs: "8px 10px", sm: "10px 12px", md: "11px 13px", lg: "10px 8px", xl: "13px 14px" },
              fontWeight: 600,
              whiteSpace: "normal",
              lineHeight: 1.3,
              // Root cause of the 3-line wrap was WIDTH, not height -- a
              // narrow column only fits one word per line, so a 3-4 word
              // label forces a 3rd line no matter what. minWidth gives each
              // column enough room to fit ~2 words per line instead.
              // Reverted to shared 150px (2026-09-04) -- a global shrink
              // here also narrowed Member/the 3 Average-*-Time columns,
              // which Rahim did NOT ask for; the narrower width now lives
              // as a per-column override on just the 4 short columns
              // (Messages sent/Assigned/Closed/Reassign Count) instead --
              // see NARROW_HEADER_CELL_SX below.
              minWidth: "150px",
              // Height cap stays as a safety net for anything longer.
              boxSizing: "content-box",
              maxHeight: "36.4px",
              overflow: "hidden",
              border: 0,
              borderBottom: "1px solid",
              borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
              // Matches Knowledge Sources' own header-band tint exactly
              // (2026-09-04, "remake the table to match" ask) -- was a very
              // close but not identical 0.04/0.025.
              // Solid backgroundColor + the same tint layered on top via
              // backgroundImage (2026-09-04, sticky-header ask) -- a bare
              // rgba backgroundColor is translucent, which is fine for a
              // static header but means body rows visibly bleed through it
              // once it's actually stuck to the top of the viewport during
              // a scroll. Same visual tint, now fully opaque.
              backgroundColor: "background.paper",
              backgroundImage: (theme) => {
                const tint = theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)";
                return `linear-gradient(${tint}, ${tint})`;
              },
            },
            "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:first-of-type": {
              borderTopLeftRadius: "12px",
              borderBottomLeftRadius: "12px",
            },
            "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-of-type": {
              borderTopRightRadius: "12px",
              borderBottomRightRadius: "12px",
            },
            // Body row divider spans the full table width, edge to edge.
            // Extra vertical padding on top of the shared padding above --
            // rows were sitting flush against each other (Rahim's ask,
            // 2026-08-17: give them room to breathe). Tightened a step
            // (2026-09-04, "reduce the spacing a little" ask) -- was
            // 16-22px. Tightened again (2026-09-05, "reduce row spacing ...
            // so a lot more data can be seen in one view") -- was 11-16px;
            // still clears the header's own tighter padding so rows don't
            // read as flush.
            "& .MuiTableBody-root .MuiTableCell-root": {
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              paddingTop: { xs: "7px", sm: "8px", md: "9px", lg: "9px", xl: "10px" },
              paddingBottom: { xs: "7px", sm: "8px", md: "9px", lg: "9px", xl: "10px" },
            },
          }}
        >
          {/* width:"fit-content" (2026-09-05, "shorten the spaces between
              the rows"/tighten Messages sent-Assigned-Closed) -- three
              layers deep to get here. (1) The old width:"100%" fought the
              per-column minWidths just below (100/80/70px, deliberately
              narrow, 2026-09-04) the moment the card around it got wider
              (today's earlier width fix) -- confirmed live, "Assigned" was
              rendering at 108px against an 80px floor. (2) Just removing
              the sx value wasn't enough either -- MUI's Table component
              ships its own baked-in default `width: 100%`, same kind of
              hidden default that broke the sticky-header work earlier
              today. (3) Even an explicit width:"auto" override didn't
              shrink it -- confirmed live, table still rendered at the
              container's full width. Root cause: per the CSS2 table-sizing
              spec, a `<table>` with computed width "auto" doesn't shrink-
              to-content like most auto-width elements -- its used width
              becomes "the greater of the containing block's width...and
              the minimum width required by the columns", i.e. it still
              fills its container by default, same as width:100% for a
              normal block box. `fit-content` is the value that actually
              asks for shrink-to-content sizing; confirmed live this is
              what finally lets individual column widths below hold instead
              of the auto-layout algorithm redistributing the container's
              full leftover width across every column regardless of what
              each one asks for. */}
          <Table stickyHeader sx={{ borderCollapse: "collapse", width: "100%" }}>
            <TableHead>
              <TableRow>
                {visibleOptionalColumns.member && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    // Capped (2026-09-05, "fit all columns without
                    // horizontal scroll" ask) -- unconstrained, this column
                    // just sat on the shared 150px minWidth floor (176px
                    // rendered) even though "Member" itself is one short
                    // word; the real driver was body-row avatar+name
                    // content having no cap of its own (see the matching
                    // cap + ellipsis added on the body Member cell below).
                    // 130px was tried first and truncated real names
                    // ("ClusterdenQA" -> "Clusterden...") -- the name is
                    // core row-identifying data, worth more than an extra
                    // narrow numeric column elsewhere, so backed off to
                    // 152px (fits a 12-char name, still meaningfully
                    // tighter than the original 176px).
                    minWidth: "152px !important",
                    maxWidth: "152px !important",
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleRequestSort("name")}
                    sx={{
                      // Default (unsorted) label matches the header row's
                      // baseline text.secondary now; active sort keeps
                      // text.primary as real emphasis, not just decoration.
                      "&.MuiTableSortLabel-root": {
                        color: "text.secondary",
                      },
                      "&.MuiTableSortLabel-root.Mui-active": {
                        color: "text.primary",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "text.secondary !important",
                        fontSize: { xs: "14px", sm: "18px", md: "14px", lg: "16px", xl: "18px" }, // Reduced for md and lg
                      },
                    }}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                )}
                {visibleOptionalColumns.inboxUsageTime && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                  }}
                >
                  <ReportsHeaderTooltip tKey="inboxUsageTime">Inbox usage time (hours)</ReportsHeaderTooltip>
                </TableCell>
                )}
                {/* These 4 short columns get a narrower minWidth than the
                    150px shared default (2026-09-04, "reduce between
                    Messages sent/Assigned/Closed/Reassign Count only" ask)
                    -- single word or short-digit content doesn't need the
                    room the longer Member/Average-*-Time headers do, and
                    Rahim explicitly did not want those others narrowed.
                    !important is required here -- the shared
                    ".MuiTableHead-root .MuiTableCell-root" rule above is a
                    2-class descendant selector, which beats a plain inline
                    sx override (1 class) on specificity alone regardless of
                    insertion order. */}
                {visibleOptionalColumns.messagesSent && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    // width (not just minWidth) added (2026-09-05, "make
                    // Messages sent into two lines ... reduce space") --
                    // minWidth alone is a floor, not a cap: table auto-layout
                    // (no table-layout:fixed on this table) still redistributes
                    // any leftover container width across every column based
                    // on content, so this was actually rendering at 128px
                    // (confirmed live) despite the 100px minWidth, wide enough
                    // for "Messages sent" to fit on one line. An explicit
                    // width pins the column instead of just flooring it, at a
                    // narrow enough value to force the same "Messages"/"sent"
                    // 2-line wrap the Average-*-Time columns already use.
                    minWidth: "80px !important",
                  }}
                >
                  <ReportsHeaderTooltip tKey="messagesSent">Messages sent</ReportsHeaderTooltip>
                </TableCell>
                )}
                {visibleOptionalColumns.assigned && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    // Same width-not-just-minWidth fix as Messages sent above
                    // -- was rendering at 108px against an 80px floor.
                    minWidth: "64px !important",
                  }}
                >
                  <ReportsHeaderTooltip tKey="assigned">Assigned</ReportsHeaderTooltip>
                </TableCell>
                )}
                {visibleOptionalColumns.closed && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    // Same width-not-just-minWidth fix as Messages sent above
                    // -- was rendering at 98px against a 70px floor.
                    minWidth: "56px !important",
                  }}
                >
                  <ReportsHeaderTooltip tKey="closed">Closed</ReportsHeaderTooltip>
                </TableCell>
                )}
                {visibleOptionalColumns.reassignCount && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    minWidth: "100px !important",
                  }}
                >
                  <ReportsHeaderTooltip tKey="reassignCount">Reassign Count</ReportsHeaderTooltip>
                </TableCell>
                )}
                {visibleOptionalColumns.responseTime && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    // Explicit cap (2026-09-05, "reduce space between icon
                    // and text") -- the real governing factor. The shared
                    // 150px minWidth this cell used to fall back on gave the
                    // inner flex row 150px of available width to compute
                    // against; since "Average Response Time" unwrapped is
                    // wider than that (its real max-content size), `fit-
                    // content` on the flex row/text span (tried first)
                    // resolved right back to that same 150px (fit-content's
                    // own formula is min(max-content, available) -- when
                    // max-content is bigger than available, as it is here,
                    // fit-content IS the available width, so it changed
                    // nothing). Capping the cell itself narrower removes
                    // that slack at the source.
                    minWidth: "112px !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.4 }}>
                    <Tooltip title={REPORTS_TOOLTIP_COPY.responseTime} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
                    <Box component="span" sx={{ textDecoration: responseTimeOperator ? "underline" : "none", color: responseTimeOperator ? "primary.main" : "inherit", fontWeight: responseTimeOperator ? 700 : "inherit", cursor: "default", width: "fit-content" }}>
                      {/* Non-breaking space between "Response" and "Time"
                          (Rahim's ask, 2026-08-17, matching the screenshot):
                          plain wrapping was breaking after "Response"
                          instead, leaving a lonely "Time" on its own line --
                          gluing the metric name into one unbreakable unit
                          forces "Average"/"Min"/etc. onto line 1 and the
                          full "Response Time" onto line 2 instead, same
                          rhythm First Response Time already wraps with. */}
                      {`${responseTimeOperator ? "" : `${responseAggMethod} `}Response Time ${columnFilterSuffix(responseTimeOperator, responseTimeValue1, responseTimeValue2, "m", "")}`.trim()}
                    </Box>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => setResponseAggAnchorEl(e.currentTarget)}
                      sx={{ p: 0, color: responseTimeOperator ? "primary.main" : "inherit" }}
                    >
                      <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>
                    </IconButton>
                  </Box>
                  {/* One combined dropdown: filter this column (operator +
                      value) AND pick how it's aggregated (Mean/Median/Min/
                      Max) -- was two separate dropdowns in two places,
                      merged into one per Rahim's ask. Popover (not Menu) so
                      the toggle/inputs can sit alongside the option list;
                      sized generously so both sections have room. */}
                  <Popover
                    anchorEl={responseAggAnchorEl}
                    open={Boolean(responseAggAnchorEl)}
                    onClose={() => setResponseAggAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: { ...reportsDropdownPaperSx, mt: 0.75 } } }}
                  >
                    {/* Restructured spacing (2026-08-17, Rahim's ask: match
                        Date Range/Select Category/All members exactly) --
                        the "Display as" MenuItems now sit directly at the
                        popover root (only their own mx:0.5 inset), same as
                        Select Category's list items, instead of being nested
                        inside the same padded Box as the Filter section
                        above, which was pushing them in an extra 14px and
                        made the two dropdowns visually inconsistent. */}
                    <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", py: 0.75 }}>
                      <Box sx={{ px: 1.75 }}>
                        <Typography sx={reportsDropdownSectionLabelSx}>Filter</Typography>
                        <DurationOperatorFilter
                          label="Response Time"
                          hideLabel
                          unit="Minutes"
                          operator={responseTimeOperator}
                          onOperatorChange={setResponseTimeOperator}
                          value1={responseTimeValue1}
                          onValue1Change={setResponseTimeValue1}
                          value2={responseTimeValue2}
                          onValue2Change={setResponseTimeValue2}
                          // No confirm button (2026-08-17) -- purely
                          // client-side now, live on every keystroke, same as
                          // Response Count below. Previously called
                          // handleApplyFilters() here, a real dispatch(fetchReports)
                          // backend round-trip on every click.
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ px: 1.75, mb: 0.5 }}>
                        <Typography sx={reportsDropdownSectionLabelSx}>Display as</Typography>
                      </Box>
                      {AGG_METHODS.map((m) => (
                        <MenuItem
                          key={m}
                          // Any operator active (>/</between) auto-deselects
                          // Average/Min/Max -- the two are mutually exclusive
                          // ways to use this column (Rahim's explicit rule,
                          // 2026-08-17). Doesn't touch the underlying
                          // responseAggMethod value, just the highlight --
                          // clearing the operator brings it back.
                          selected={!responseTimeOperator && m === responseAggMethod}
                          onClick={() => handleSelectResponseAgg(m)}
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: !responseTimeOperator && m === responseAggMethod ? 600 : 400,
                            py: 0.95,
                            px: 1.625,
                            mb: 0.25,
                            mx: 0.5,
                            borderRadius: "8px",
                            "&.Mui-selected, &.Mui-selected:hover": reportsDropdownSelectedSx,
                          }}
                        >
                          {m}
                        </MenuItem>
                      ))}
                    </Box>
                  </Popover>
                </TableCell>
                )}
                {visibleOptionalColumns.resolutionTime && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px", // Small/Label token; weight carries the header emphasis
                    // Same explicit cap as Average Response Time above --
                    // see that cell's comment for the full root-cause.
                    minWidth: "112px !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.4 }}>
                    <Tooltip title={REPORTS_TOOLTIP_COPY.resolutionTime} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
                    <Box component="span" sx={{ textDecoration: resolutionTimeOperator ? "underline" : "none", color: resolutionTimeOperator ? "primary.main" : "inherit", fontWeight: resolutionTimeOperator ? 700 : "inherit", cursor: "default", width: "fit-content" }}>
                      {`${resolutionTimeOperator ? "" : `${resolutionAggMethod} `}Resolution Time ${columnFilterSuffix(resolutionTimeOperator, resolutionTimeValue1, resolutionTimeValue2, "m", "")}`.trim()}
                    </Box>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => setResolutionAggAnchorEl(e.currentTarget)}
                      sx={{ p: 0, color: resolutionTimeOperator ? "primary.main" : "inherit" }}
                    >
                      <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>
                    </IconButton>
                  </Box>
                  <Popover
                    anchorEl={resolutionAggAnchorEl}
                    open={Boolean(resolutionAggAnchorEl)}
                    onClose={() => setResolutionAggAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: { ...reportsDropdownPaperSx, mt: 0.75 } } }}
                  >
                    <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", py: 0.75 }}>
                      <Box sx={{ px: 1.75 }}>
                        <Typography sx={reportsDropdownSectionLabelSx}>Filter</Typography>
                        <DurationOperatorFilter
                          label="Resolution Time"
                          hideLabel
                          unit="Minutes"
                          operator={resolutionTimeOperator}
                          onOperatorChange={setResolutionTimeOperator}
                          value1={resolutionTimeValue1}
                          onValue1Change={setResolutionTimeValue1}
                          value2={resolutionTimeValue2}
                          onValue2Change={setResolutionTimeValue2}
                          // No confirm button (2026-08-17) -- purely
                          // client-side now, same as Response Time above.
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ px: 1.75, mb: 0.5 }}>
                        <Typography sx={reportsDropdownSectionLabelSx}>Display as</Typography>
                      </Box>
                      {AGG_METHODS.map((m) => (
                        <MenuItem
                          key={m}
                          selected={!resolutionTimeOperator && m === resolutionAggMethod}
                          onClick={() => handleSelectResolutionAgg(m)}
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: !resolutionTimeOperator && m === resolutionAggMethod ? 600 : 400,
                            py: 0.95,
                            px: 1.625,
                            mb: 0.25,
                            mx: 0.5,
                            borderRadius: "8px",
                            "&.Mui-selected, &.Mui-selected:hover": reportsDropdownSelectedSx,
                          }}
                        >
                          {m}
                        </MenuItem>
                      ))}
                    </Box>
                  </Popover>
                </TableCell>
                )}

                {/* First Response Time / Response Count -- new Overview
                    columns (2026-08-12). Column VALUES are real (Phase 1 +
                    Phase 2, 2026-08-15). Header dropdown's Filter+Display-as
                    UI still doesn't dispatch a fetch on the operator/value
                    inputs -- no backend filtering wired for these columns yet. */}
                {visibleOptionalColumns.firstResponseTime && (
                <TableCell
                  sx={{
                    color: "text.secondary", fontWeight: 600, fontSize: "13px",
                    // Same fix as Average Response Time above (see that
                    // cell's comment for the root-cause), but wider --
                    // 112px was too narrow for "First Response Time" (one
                    // more word than "Response Time"/"Resolution Time"),
                    // forcing an actual 3rd wrapped line ("Average" /
                    // "First Response" / "Time") instead of 2, which broke
                    // the flex-end vertical alignment fix differently.
                    minWidth: "140px !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.4 }}>
                    <Tooltip title={REPORTS_TOOLTIP_COPY.firstResponseTime} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
                    <Box component="span" sx={{ textDecoration: firstResponseTimeOperator ? "underline" : "none", color: firstResponseTimeOperator ? "primary.main" : "inherit", fontWeight: firstResponseTimeOperator ? 700 : "inherit", cursor: "default", width: "fit-content" }}>
                      {`${firstResponseTimeOperator ? "" : `${firstResponseAggMethod} `}First Response Time ${columnFilterSuffix(firstResponseTimeOperator, firstResponseTimeValue1, firstResponseTimeValue2, "m", "")}`.trim()}
                    </Box>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => setFirstResponseAnchorEl(e.currentTarget)}
                      sx={{ p: 0, color: firstResponseTimeOperator ? "primary.main" : "inherit" }}
                    >
                      <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>
                    </IconButton>
                  </Box>
                  <Popover
                    anchorEl={firstResponseAnchorEl}
                    open={Boolean(firstResponseAnchorEl)}
                    onClose={() => setFirstResponseAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: { ...reportsDropdownPaperSx, mt: 0.75 } } }}
                  >
                    <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", py: 0.75 }}>
                      <Box sx={{ px: 1.75 }}>
                        <Typography sx={reportsDropdownSectionLabelSx}>Filter</Typography>
                        {/* No backend for this column yet -- operator/value
                            selection is live in the header label above, just
                            not wired to fetchReports (same as before, unrelated
                            to the tick-button/styling cleanup here). */}
                        <DurationOperatorFilter
                          label="First Response Time"
                          hideLabel
                          unit="Minutes"
                          operator={firstResponseTimeOperator}
                          onOperatorChange={setFirstResponseTimeOperator}
                          value1={firstResponseTimeValue1}
                          onValue1Change={setFirstResponseTimeValue1}
                          value2={firstResponseTimeValue2}
                          onValue2Change={setFirstResponseTimeValue2}
                          // No confirm button (2026-08-17) -- matches
                          // Response/Resolution Time above, no reason to be
                          // the odd one out.
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ px: 1.75, mb: 0.5 }}>
                        <Typography sx={reportsDropdownSectionLabelSx}>Display as</Typography>
                      </Box>
                      {AGG_METHODS.map((m) => (
                        <MenuItem
                          key={m}
                          selected={!firstResponseTimeOperator && m === firstResponseAggMethod}
                          onClick={() => handleSelectFirstResponseAgg(m)}
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: !firstResponseTimeOperator && m === firstResponseAggMethod ? 600 : 400,
                            py: 0.95,
                            px: 1.625,
                            mb: 0.25,
                            mx: 0.5,
                            borderRadius: "8px",
                            "&.Mui-selected, &.Mui-selected:hover": reportsDropdownSelectedSx,
                          }}
                        >
                          {m}
                        </MenuItem>
                      ))}
                    </Box>
                  </Popover>
                </TableCell>
                )}
                {visibleOptionalColumns.responseCount && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px",
                    // Capped narrower (2026-09-05, "fit all columns without
                    // horizontal scroll" ask) -- left unconstrained, this
                    // column sat on the shared 150px minWidth floor but
                    // rendered wider still (176px), since auto-layout gives
                    // it exactly enough room for "Response Count" to sit on
                    // ONE line -- there's a real space between the two words
                    // for the header's own whiteSpace:"normal" to wrap on,
                    // it just never gets narrow enough to need to. A cap
                    // forces that natural break into a real 2-line wrap
                    // ("Response"/"Count"), the same technique already used
                    // on the 3 Average-*-Time columns below.
                    minWidth: "104px !important",
                    maxWidth: "104px !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <Tooltip title={REPORTS_TOOLTIP_COPY.responseCount} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
                    <Box component="span" sx={{ textDecoration: responseCountOperator ? "underline" : "none", color: responseCountOperator ? "primary.main" : "inherit", fontWeight: responseCountOperator ? 700 : "inherit", cursor: "default" }}>
                      {`Response Count ${columnFilterSuffix(responseCountOperator, responseCountValue1, responseCountValue2, "m", "")}`.trim()}
                    </Box>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => setResponseCountAnchorEl(e.currentTarget)}
                      sx={{ p: 0, color: responseCountOperator ? "primary.main" : "inherit" }}
                    >
                      <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>
                    </IconButton>
                  </Box>
                  <Popover
                    anchorEl={responseCountAnchorEl}
                    open={Boolean(responseCountAnchorEl)}
                    onClose={() => setResponseCountAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: { ...reportsDropdownPaperSx, mt: 0.75 } } }}
                  >
                    <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", p: 1.75 }}>
                      <Typography sx={reportsDropdownSectionLabelSx}>Filter</Typography>
                      <DurationOperatorFilter
                        label="Response Count"
                        hideLabel
                        // Same "Duration (Minutes)" field label as the Time
                        // filters (2026-08-17, Rahim's ask) -- Count filters
                        // ARE a minutes threshold under the hood too (counts
                        // how many of the member's individual events were
                        // under/over it), so showing the raw column name
                        // here instead read as inconsistent/wrong.
                        unit="Minutes"
                        operator={responseCountOperator}
                        onOperatorChange={setResponseCountOperator}
                        value1={responseCountValue1}
                        onValue1Change={setResponseCountValue1}
                        value2={responseCountValue2}
                        onValue2Change={setResponseCountValue2}
                        // No confirm button (Rahim's ask, 2026-08-17) --
                        // purely client-side, already live on every
                        // keystroke, nothing to confirm. Popover closes on
                        // click-away like any other dropdown.
                      />
                    </Box>
                  </Popover>
                </TableCell>
                )}

                {/* Resolution Count / First Response Count -- new Overview
                    columns (2026-08-12). Customize columns checkbox is
                    functional (2026-08-15) -- unchecking one really hides
                    that column, persisted via localStorage.
                    Average/Min/Max deliberately removed from all 3 Count
                    columns (2026-08-15) -- each member only ever has ONE
                    count value, there's no spread of multiple numbers for
                    Min/Max to mean anything different from Average; the
                    control was a dead copy-paste from the Time columns
                    (confirmed live: switching it never changed the number
                    shown). */}
                {visibleOptionalColumns.resolutionCount && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px",
                    // Same cap as Response Count above -- "Resolution" is a
                    // couple px wider than "Response" but wraps fine at the
                    // same 104px.
                    minWidth: "104px !important",
                    maxWidth: "104px !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <Tooltip title={REPORTS_TOOLTIP_COPY.resolutionCount} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
                    <Box component="span" sx={{ textDecoration: resolutionCountOperator ? "underline" : "none", color: resolutionCountOperator ? "primary.main" : "inherit", fontWeight: resolutionCountOperator ? 700 : "inherit", cursor: "default" }}>
                      {`Resolution Count ${columnFilterSuffix(resolutionCountOperator, resolutionCountValue1, resolutionCountValue2, "m", "")}`.trim()}
                    </Box>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => setResolutionCountAnchorEl(e.currentTarget)}
                      sx={{ p: 0, color: resolutionCountOperator ? "primary.main" : "inherit" }}
                    >
                      <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>
                    </IconButton>
                  </Box>
                  <Popover
                    anchorEl={resolutionCountAnchorEl}
                    open={Boolean(resolutionCountAnchorEl)}
                    onClose={() => setResolutionCountAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: { ...reportsDropdownPaperSx, mt: 0.75 } } }}
                  >
                    <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", p: 1.75 }}>
                      <Typography sx={reportsDropdownSectionLabelSx}>Filter</Typography>
                      <DurationOperatorFilter
                        label="Resolution Count"
                        hideLabel
                        unit="Minutes"
                        operator={resolutionCountOperator}
                        onOperatorChange={setResolutionCountOperator}
                        value1={resolutionCountValue1}
                        onValue1Change={setResolutionCountValue1}
                        value2={resolutionCountValue2}
                        onValue2Change={setResolutionCountValue2}
                        // No confirm button -- purely client-side, already live.
                      />
                    </Box>
                  </Popover>
                </TableCell>
                )}
                {visibleOptionalColumns.firstResponseCount && (
                <TableCell
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13px",
                    // 3-word label -- capped wider than the 2-word Count
                    // columns (128px) so "Response\u00a0Count" (glued,
                    // see below) fits on its own line without forcing a
                    // 3rd line.
                    minWidth: "128px !important",
                    maxWidth: "128px !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <Tooltip title={REPORTS_TOOLTIP_COPY.firstResponseCount} placement="top" arrow componentsProps={reportsInfoTooltipSx}>
                    <Box component="span" sx={{ textDecoration: firstResponseCountOperator ? "underline" : "none", color: firstResponseCountOperator ? "primary.main" : "inherit", fontWeight: firstResponseCountOperator ? 700 : "inherit", cursor: "default" }}>
                      {`First Response Count ${columnFilterSuffix(firstResponseCountOperator, firstResponseCountValue1, firstResponseCountValue2, "m", "")}`.trim()}
                    </Box>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => setFirstResponseCountAnchorEl(e.currentTarget)}
                      sx={{ p: 0, color: firstResponseCountOperator ? "primary.main" : "inherit" }}
                    >
                      <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>
                    </IconButton>
                  </Box>
                  <Popover
                    anchorEl={firstResponseCountAnchorEl}
                    open={Boolean(firstResponseCountAnchorEl)}
                    onClose={() => setFirstResponseCountAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    slotProps={{ paper: { sx: { ...reportsDropdownPaperSx, mt: 0.75 } } }}
                  >
                    <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", p: 1.75 }}>
                      <Typography sx={reportsDropdownSectionLabelSx}>Filter</Typography>
                      <DurationOperatorFilter
                        label="First Response Count"
                        hideLabel
                        unit="Minutes"
                        operator={firstResponseCountOperator}
                        onOperatorChange={setFirstResponseCountOperator}
                        value1={firstResponseCountValue1}
                        onValue1Change={setFirstResponseCountValue1}
                        value2={firstResponseCountValue2}
                        onValue2Change={setFirstResponseCountValue2}
                        // No confirm button -- purely client-side, already live.
                      />
                    </Box>
                  </Popover>
                </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row) => {
                return (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {visibleOptionalColumns.member && (
                  <TableCell sx={{ maxWidth: "152px !important", overflow: "hidden" }}>
                    <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                      {/* Flattened to 32px — splits the old mobile (28px) and
                          desktop (40px) values, reads fine at both instead of
                          ballooning on desktop or feeling cramped on mobile. */}
                      <Avatar
                        sx={{
                          bgcolor: getRandomAvatarColor(row.name),
                          mr: 1.5,
                          width: 32,
                          height: 32,
                          fontSize: "14px",
                          flexShrink: 0,
                        }}
                      >
                        {row.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        // Name truncates with an ellipsis instead of forcing
                        // the column wider (2026-09-05, matching the header
                        // cap above) -- minWidth:0 on the flex parent is
                        // required or a flex child never actually shrinks
                        // below its content's natural width.
                        sx={{
                          fontSize: "14px", // matches body size; weight (600) carries the emphasis
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth: 0,
                        }}
                      >
                        {row.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  )}
                  {visibleOptionalColumns.inboxUsageTime && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {formatHoursAndMinutes(row.totalLoginDuration)}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.messagesSent && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.messageStampsCount}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.assigned && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.assignedCount}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.closed && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.closedCount}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.reassignCount && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.reassignStampsCount}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.responseTime && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.messagesWithResponse
                      ? formatMinutesAndSeconds(getAggregationValue(row, "ResponseTime", responseAggMethod))
                      : "—"}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.resolutionTime && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.resolutionTimeCount
                      ? formatMinutesAndSeconds(getAggregationValue(row, "ResolutionTime", resolutionAggMethod))
                      : "—"}
                  </TableCell>
                  )}
                  {/* Response/Resolution/First Response Count all wired to
                      real data (Phase 1 + Phase 2, 2026-08-15) -- every
                      field here already exists on the API response, same
                      fields already used above for Messages Sent /
                      Resolution Time. */}
                  {visibleOptionalColumns.firstResponseTime && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {row.firstResponseTimeCount
                      ? formatMinutesAndSeconds(getAggregationValue(row, "FirstResponseTime", firstResponseAggMethod))
                      : "—"}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.responseCount && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {responseCountOperator ? getQualifyingResponseCount(row) : row.messageStampsCount}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.resolutionCount && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {resolutionCountOperator ? getQualifyingResolutionCount(row) : row.resolutionTimeCount}
                  </TableCell>
                  )}
                  {visibleOptionalColumns.firstResponseCount && (
                  <TableCell sx={{ color: "text.primary" }}>
                    {firstResponseCountOperator ? getQualifyingFirstResponseCount(row) : row.firstResponseTimeCount}
                  </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* Moved out from inside the table (was a colSpan'd TableRow) --
              2026-09-05, Rahim's screenshot: text was centering on the
              FULL scrollable table width (all ~11 columns, several scrolled
              off-screen), not the visible card, so it read as pushed off
              to one side whenever the columns didn't all fit in view. A
              colSpan cell can only center within the table's own (wide,
              scrollable) content box -- moving this to a plain sibling Box
              here means it sizes to TableContainer's own box instead (the
              actual visible card width), which is what should be centered
              on. No visual change intended otherwise -- same copy, same
              position directly under the header, same spacing. */}
          {sortedData.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: 8 }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "text.primary" }}>
                No members found
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                Try adjusting or clearing your filters.
              </Typography>
            </Box>
          )}
        </TableContainer>
        </>
        )}
      </Paper>
    </>
  );
};

export default ReportsTable;
